import posthogClient from 'posthog-js';
import {
	ATTRIBUTION_PERSON_PROPERTIES,
	allowsPersonProfile,
	getMarketingConsentState,
	hasAdAttributionSignal,
	withdrawsPersonProfile
} from '../helpers/adAttributionHelpers';
import {META_PROPERTY_NAMES, getMetaCookieProperties} from '../helpers/metaCookieHelpers';
import {getUpRefUid} from '../helpers/upRefCookieHelpers';

/** @typedef {import('posthog-js').CaptureResult} CaptureResult */

/**
 * @typedef {Object} TrackingCaptureOptions
 * @property {Date} [timestamp]
 * @property {boolean} [flushOnPageHide=false]
 */

/**
 * @typedef {Object} FeatureFlagsLoadResult
 * @property {boolean} loaded
 * @property {'disabled'|'request'|'timeout'|null} failureReason
 */

const POSTHOG_SERVICE_NAME                  = 'Storefront frontend',
      FEATURE_FLAG_PROPERTY_PREFIX          = '$feature/',
      FEATURE_FLAG_REQUEST_TIMEOUT_MS       = 3000,
      FEATURE_FLAG_CALLBACK_GRACE_PERIOD_MS = 100,
      FEATURE_FLAG_READINESS_TIMEOUT_MS     = FEATURE_FLAG_REQUEST_TIMEOUT_MS +
                                              FEATURE_FLAG_CALLBACK_GRACE_PERIOD_MS,

      /*
       * Meta's pixel is loaded through the tag manager, so `_fbp` / `_fbc` can
       * appear after PostHog has already initialised. Poll on a short, bounded
       * schedule: GTM typically has fbevents.js up within about a second on a
       * warm cache and within a few seconds on a cold load, so the first two
       * milestones cover the realistic injection window. The cutoff ends
       * polling for the pathological case; after it, the identifiers are still
       * picked up by the fresh cookie read that every tracked event performs.
       */
      EARLY_PIXEL_RETRY_DELAY_IN_MS = 1000,
      SLOW_PIXEL_RETRY_DELAY_IN_MS  = 3000,
      META_COOKIE_POLL_CUTOFF_IN_MS = 10_000,

      META_COOKIE_RETRY_DELAYS_IN_MS = [
	      EARLY_PIXEL_RETRY_DELAY_IN_MS,
	      SLOW_PIXEL_RETRY_DELAY_IN_MS,
	      META_COOKIE_POLL_CUTOFF_IN_MS
      ],

      // Cookiebot dispatches these on every consent decision; withdrawal
      // removes the Meta cookies, so each one triggers a sync. Accept also
      // fires on every later page load with a stored or out-of-region
      // decision, so the person-profile sync rides the same two events.
      COOKIEBOT_CONSENT_EVENTS = ['CookiebotOnAccept', 'CookiebotOnDecline'],

      // Without a current decision (a lapsed consent, or one reset by a new
      // consent version) uc.js dispatches neither of the above; it opens its
      // banner, whose script dispatches this. The person profile re-syncs on
      // it (uc.js 2.138.0 / cc.js, verified 2026-09-30). It also fires when a
      // visitor reopens the dialog to review a stored decision
      // (Cookiebot.show() / renew()); that decision stays on record until
      // they submit (hasDecisionOnRecord), so a review changes nothing.
      COOKIEBOT_BANNER_EVENT = 'CookiebotOnDialogDisplay',

      // posthog-js 1.396.4 persistence key (ENABLE_PERSON_PROCESSING) that
      // createPersonProfile() sets; sticky until unregistered.
      PERSON_PROCESSING_FLAG = '$epp',

      // posthog-js 1.396.4 persistence key (INITIAL_PERSON_INFO): {r: referrer,
      // u: landing URL} of the first captured page, the source of every
      // $initial_* person property.
      INITIAL_PERSON_INFO = '$initial_person_info';

class PosthogService {
	constructor() {
		this.isInitialized = false;
		this.areFeatureFlagsLoaded = false;
		this.featureFlagsLoadPromise = null;
		/** @type {Array<[string, boolean|string|undefined]>|null} */
		this.experimentEventProperties = null;
		/** @type {Map<string, boolean|string>} */
		this.pendingExperimentExposures = new Map();
		// A withdrawal on this page spends posthog-js's once-per-page send of
		// the stored first touch (see syncPersonProfileWithConsent).
		this.hasWithdrawnPersonProfile = false;
		this.visitorId = getUpRefUid();

		this.init();
	}

	init() {
		if (this.isInitialized || !this.isEnabled()) {
			return;
		}

		this.isInitialized = true;

		posthogClient.init(
			window.globalParams.POSTHOG_PROJECT_KEY,
			{
				'api_host' : window.globalParams.POSTHOG_API_HOST,
				'defaults' : window.globalParams.POSTHOG_DEFAULTS,
				'bootstrap': {
					'distinctID'    : this.visitorId,
					'isIdentifiedID': false
				},
				'advanced_disable_feature_flags_on_first_load': true,
				'feature_flag_request_timeout_ms'             : FEATURE_FLAG_REQUEST_TIMEOUT_MS,
				'autocapture'                                 : false,
				'capture_pageview'                            : 'history_change',
				'before_send'                                 : (event) => this._applyExperimentAssignments(event),
				'capture_exceptions'                          : {
					'capture_console_errors'      : true,
					'capture_unhandled_errors'    : true,
					'capture_unhandled_rejections': true
				},
				'opt_out_capturing_by_default': false,
				'loaded'                      : () => {
					this.registerInitialSuperProperties();
					this.syncPersonProfileWithConsent();
					this.scheduleMetaCookieRetries();
					this.listenForConsentChanges();
					this.loadFeatureFlags();
					this.optInCapturing();
				}
			}
		);
	}

	/**
	 * @returns {boolean}
	 */
	isEnabled() {
		return window.globalParams.trackingAllowed !== false && !!window.globalParams.POSTHOG_PROJECT_KEY;
	}

	/**
	 * @param {string} eventName
	 * @param {!Object} [properties]
	 * @param {TrackingCaptureOptions} [options]
	 */
	track(eventName, properties = {}, {timestamp, flushOnPageHide = false} = {}) {
		if (!eventName || !this.isEnabled()) {
			return;
		}

		this.init();
		posthogClient.capture(eventName, this.getEventProperties(properties, this.syncMetaCookieProperties()), {
			timestamp,
			...(flushOnPageHide ? {'send_instantly': true, 'transport': 'sendBeacon'} : {})
		});
	}

	/**
	 * Publish the finalized PostHog values used by the page. An undefined value
	 * explicitly excludes an experiment without a confirmed PostHog assignment.
	 * @param {Object<string, boolean|string|undefined>} assignments
	 * @param {string[]} [deferredExposureTests=[]]
	 */
	setExperimentAssignments(assignments, deferredExposureTests = []) {
		this.pendingExperimentExposures.clear();
		this.experimentEventProperties = Object.entries(assignments).map(([testName, variation]) => {
			if (variation !== undefined && deferredExposureTests.includes(testName)) {
				this.pendingExperimentExposures.set(testName, variation);

				return [`${FEATURE_FLAG_PROPERTY_PREFIX}${testName}`, undefined];
			}

			return [`${FEATURE_FLAG_PROPERTY_PREFIX}${testName}`, variation];
		});
	}

	/**
	 * Expose a deferred experiment once the UI using its assignment is rendered.
	 * Call after assignment resolution; use the pinned value even after SDK refreshes.
	 * @param {string} testName
	 */
	exposeExperiment(testName) {
		if (this.experimentEventProperties === null) {
			console.error('Experiment assignments are not ready');

			return;
		}

		const variation = this.pendingExperimentExposures.get(testName);

		if (variation === undefined) {
			return;
		}

		this.pendingExperimentExposures.delete(testName);
		this.experimentEventProperties = this.experimentEventProperties.map(([key, value]) => (
			[key, key === `${FEATURE_FLAG_PROPERTY_PREFIX}${testName}` ? variation : value]
		));
		this.track('$feature_flag_called', {
			'$feature_flag'         : testName,
			'$feature_flag_response': variation
		});
	}

	/**
	 * The SDK adds cached flags after merging event properties. Sanitize the final
	 * payload, including automatic pageviews, then pin experiments to the confirmed
	 * PostHog assignments used by the page even if the SDK refreshes flags later.
	 * @param {CaptureResult|null} event
	 * @returns {CaptureResult|null}
	 * @private
	 */
	_applyExperimentAssignments(event) {
		if (!event) {
			return event;
		}

		// A successful flag response alone does not establish a page assignment.
		// Keep events unattributed until resolution succeeds, including on config errors.
		if (!this.areFeatureFlagsLoaded || this.experimentEventProperties === null) {
			Object.keys(event.properties)
				.filter((key) => key.startsWith(FEATURE_FLAG_PROPERTY_PREFIX))
				.forEach((key) => delete event.properties[key]);
		}

		this.experimentEventProperties?.forEach(([key, variation]) => {
			if (variation === undefined) {
				delete event.properties[key];
			} else {
				event.properties[key] = variation;
			}
		});

		return event;
	}

	/**
	 * @returns {string}
	 */
	getDistinctId() {
		if (!this.isEnabled()) {
			return this.visitorId;
		}

		this.init();

		return posthogClient.get_distinct_id() || this.visitorId;
	}

	/**
	 * @param {string} flagKey
	 * @param {{sendExposure?: boolean}} [options]
	 * @returns {boolean|string|undefined}
	 */
	getServerLoadedFeatureFlag(flagKey, {sendExposure = true} = {}) {
		if (!flagKey || typeof flagKey !== 'string') {
			throw new TypeError('Feature flag key must be a non-empty string');
		}

		if (!this.isEnabled() || !this.areFeatureFlagsLoaded) {
			return undefined;
		}

		this.init();

		return posthogClient.getFeatureFlag(flagKey, {'fresh': true, 'send_event': sendExposure});
	}

	/**
	 * @returns {Promise<FeatureFlagsLoadResult>}
	 */
	loadFeatureFlags() {
		if (!this.isEnabled()) {
			return Promise.resolve({
				'loaded'       : false,
				'failureReason': 'disabled'
			});
		}

		this.init();

		if (!this.featureFlagsLoadPromise) {
			this.featureFlagsLoadPromise = this._requestFeatureFlags();
		}

		return this.featureFlagsLoadPromise;
	}

	/**
	 * @returns {Promise<FeatureFlagsLoadResult>}
	 * @private
	 */
	_requestFeatureFlags() {
		let timeoutId,
		    unsubscribe;

		const timeoutPromise = new Promise((resolve) => {
			      timeoutId = setTimeout(
				      () => resolve({
					      'loaded'       : false,
					      'failureReason': 'timeout'
				      }),
				      FEATURE_FLAG_READINESS_TIMEOUT_MS
			      );
		      }),
		      featureFlagsPromise = new Promise((resolve) => {
			      unsubscribe = posthogClient.onFeatureFlags((...callbackArguments) => {
				      const context = callbackArguments.pop();

				      if (!context || typeof context.errorsLoading !== 'boolean') {
					      return;
				      }

				      resolve({
					      'loaded'       : context.errorsLoading !== true,
					      'failureReason': context.errorsLoading === true ? 'request' : null
				      });
			      });
			      posthogClient.reloadFeatureFlags();
		      });

		return Promise.race([featureFlagsPromise, timeoutPromise])
		              .catch((err) => {
			              console.error(err);

			              return {
				              'loaded'       : false,
				              'failureReason': 'request'
			              };
		              })
		              .then((result) => {
			              this.areFeatureFlagsLoaded = result.loaded;

			              return result;
		              })
		              .finally(() => {
			              clearTimeout(timeoutId);
			              unsubscribe();
		              });
	}

	/**
	 * @param {*} error
	 * @param {!Object} [additionalProperties]
	 */
	captureError(error, additionalProperties = {}) {
		if (!this.isEnabled()) {
			console.error(error);

			return;
		}

		this.init();
		posthogClient.captureException(
			this.normalizeError(error),
			this.getEventProperties(additionalProperties, this.syncMetaCookieProperties())
		);
	}

	/**
	 * One persistence write for the whole initial super-property bag. The SDK
	 * saves on every register() call (no debounce under the pinned defaults
	 * date) and this runs before the automatic pageview is emitted, so the
	 * common and Meta properties are registered as one union rather than one
	 * call per property. Only the common bag feeds flag evaluation.
	 *
	 * The auto-captured pageview only carries the Meta identifiers when the
	 * cookies already exist here: optInCapturing() captures it synchronously
	 * at the end of `loaded`, before the first poll can run, so a pixel that
	 * writes its cookies late misses that one event. That limitation is
	 * accepted; the identifiers exist for conversion matching, and every
	 * tracked event syncs from a fresh cookie read.
	 */
	registerInitialSuperProperties() {
		const commonProperties = {
			      ...window.globalParams.trackingProperties,
			      'service'   : POSTHOG_SERVICE_NAME,
			      'labelId'   : window.globalParams.labelId,
			      'pageType'  : window.globalParams.pageType,
			      'siteLocale': window.globalParams.siteLocale,
			      'uid'       : this.visitorId
		      },
		      metaCookieProperties = getMetaCookieProperties();

		posthogClient.register({...commonProperties, ...metaCookieProperties});
		posthogClient.setPersonPropertiesForFlags(commonProperties, false);
		this.unregisterAbsentMetaProperties(metaCookieProperties);
	}

	/**
	 * Mirror the current cookie state into the persisted super properties and
	 * return the snapshot, so callers attach the exact state they synced.
	 *
	 * The SDK merges persisted properties first and event properties last, and
	 * an omitted event key does NOT override a persisted one. An absent cookie
	 * therefore has to actively unregister: without that, a value captured
	 * before consent was withdrawn would ride every later event, including
	 * SDK-internal captures that never pass through track().
	 *
	 * @returns {{fbp?: string, fbc?: string}}
	 */
	syncMetaCookieProperties() {
		const metaCookieProperties = getMetaCookieProperties();

		if (Object.keys(metaCookieProperties).length) {
			posthogClient.register(metaCookieProperties);
		}

		this.unregisterAbsentMetaProperties(metaCookieProperties);

		return metaCookieProperties;
	}

	/**
	 * The SDK's unregister is already a persistence no-op for a key it never
	 * stored (it checks `prop in props` before saving), so the absent keys are
	 * passed straight through without a get_property guard.
	 *
	 * @param {{fbp?: string, fbc?: string}} metaCookieProperties
	 */
	unregisterAbsentMetaProperties(metaCookieProperties) {
		META_PROPERTY_NAMES
			.filter((propertyName) => !metaCookieProperties[propertyName])
			.forEach((propertyName) => posthogClient.unregister?.(propertyName));
	}

	/**
	 * The pixel may set its cookies after we initialise, so re-read them at
	 * each configured milestone. Re-registering is idempotent, so the polls
	 * are left to run out rather than cancelled.
	 *
	 * Skipped entirely when both cookies were already there on load, which is
	 * the common case for a returning visitor.
	 */
	scheduleMetaCookieRetries() {
		if (Object.keys(getMetaCookieProperties()).length === META_PROPERTY_NAMES.length) {
			return;
		}

		META_COOKIE_RETRY_DELAYS_IN_MS.forEach((delay) => {
			window.setTimeout(
				() => this.syncMetaCookieProperties(),
				delay
			);
		});
	}

	/**
	 * Consent withdrawal removes the Meta cookies; syncing on Cookiebot's own
	 * events clears the persisted copies immediately, so SDK-internal captures
	 * that bypass track() cannot re-attach a stale value in the window before
	 * the next tracked event. The person profile follows the same decision.
	 */
	listenForConsentChanges() {
		COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
			window.addEventListener(eventName, () => {
				this.syncMetaCookieProperties();
				this.syncPersonProfileWithConsent();
			});
		});
		window.addEventListener(COOKIEBOT_BANNER_EVENT, () => this.syncPersonProfileWithConsent());
	}

	/**
	 * SPE-4047: restore person profiles for attributable visitors without an
	 * identify() (SPE-3898 keeps the bootstrap distinct id), and only with
	 * marketing consent where a CMP exists. createPersonProfile() is a no-op
	 * once $epp is persisted and sends one $set carrying the stored first-touch
	 * attribution ($initial_person_info is registered by the first capture on
	 * the landing page, i.e. the auto pageview or this $set). In `loaded` it
	 * runs before optInCapturing(), which synchronously captures the landing
	 * pageview, so that pageview is person-processed too, and its flags reload
	 * is debounced into the one loadFeatureFlags() issues right after; from a
	 * later Cookiebot event the SDK reloads flags on its own, which cannot move
	 * the page off its pinned assignments. Withdrawal follows a declined
	 * decision, and on a CMP label also the absence of any decision once
	 * Cookiebot has loaded (withdrawsPersonProfile; a stored decision under
	 * review is still on record): the click-id and URL person properties are
	 * unset and person processing is switched off again;
	 * unsetPersonProperties() would itself enable person processing, hence
	 * the $epp guard. PostHog resolves a $set/$set_once and $unset of one key
	 * on that event against the person as "present means gone, absent means
	 * the value lands", so a key the person lacks but this page's campaign
	 * params carry lands with the withdrawal (a documented limit, see the
	 * PRD). A re-accept on the same page sends the stored first touch itself,
	 * so the person gets its $initial_* back without a reload.
	 */
	syncPersonProfileWithConsent() {
		const consent = getMarketingConsentState();

		if (allowsPersonProfile(consent) && hasAdAttributionSignal(posthogClient.get_property(INITIAL_PERSON_INFO))) {
			if (this.hasWithdrawnPersonProfile && !posthogClient.get_property(PERSON_PROCESSING_FLAG)) {
				// Re-accept after a withdrawal on this page (the booking SPA never
				// reloads): posthog-js attaches the stored first touch only to the
				// first person-processed event of a page, which has gone out, so
				// createPersonProfile() would send a $set without it and PostHog
				// would take $initial_current_url & co. from the current page. Pass
				// the SDK's own derivation of $initial_person_info instead.
				posthogClient.setPersonProperties({}, posthogClient.persistence?.get_initial_props());
			} else {
				posthogClient.createPersonProfile();
			}

			return;
		}

		if (withdrawsPersonProfile(consent) && posthogClient.get_property(PERSON_PROCESSING_FLAG)) {
			posthogClient.unsetPersonProperties([...ATTRIBUTION_PERSON_PROPERTIES]);
			posthogClient.unregister(PERSON_PROCESSING_FLAG);
			this.hasWithdrawnPersonProfile = true;
		}
	}

	optInCapturing() {
		posthogClient.opt_in_capturing({'captureEventName': false});
	}

	/**
	 * @param {!Object} properties
	 * @param {{fbp?: string, fbc?: string}} [metaCookieProperties] the snapshot
	 * the caller just synced via syncMetaCookieProperties, so the persisted
	 * super properties and this event agree on one read
	 * @returns {!Object}
	 */
	getEventProperties(properties, metaCookieProperties = getMetaCookieProperties()) {
		// `fbp` / `fbc` are reserved: they only ever come out of the validated
		// cookie read, so a caller-supplied value cannot bypass that validation.
		const callerProperties = {...properties};

		META_PROPERTY_NAMES.forEach((propertyName) => delete callerProperties[propertyName]);

		return {
			'service'   : POSTHOG_SERVICE_NAME,
			'uid'       : this.visitorId,
			'labelId'   : window.globalParams.labelId,
			'pageType'  : window.globalParams.pageType,
			'siteLocale': window.globalParams.siteLocale,
			...callerProperties,
			...metaCookieProperties
		};
	}

	/**
	 * @param {*} error
	 * @returns {Error}
	 */
	normalizeError(error) {
		if (error instanceof Error) {
			return error;
		}

		return new Error(String(error));
	}
}

export default new PosthogService();